import { type IDropdownOption, IStackItemStyles, MessageBarType, PanelType, Separator, Stack } from '@fluentui/react';
import { Accordion, Button, buttonStylesPrimary, KeyValuePairEditor, Panel, TextField, useToast } from '@h2oai/ui-kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { roleBindingResourceCompile } from '../../../../authz/authz.functions';
import {
  useMutationAddTagsToWorkspace,
  useMutationCreateWorkspace,
  useMutationRemoveTagsFromWorkspace,
  useMutationUpdateWorkspace,
  useQueryPermissionCurrentUser,
} from '../../../../authz/authz.hooks';
import type { RoleBinding } from '../../../../authz/gen/ai/h2o/authorization/v1/role_binding_pb';
import { useRoles } from '../../../../authz/providers/RoleProvider';
import { WorkspacesUserActionType } from '../../../../authz/userActions';
import { useUser } from '../../../../utils/hooks';
import { formatError } from '../../../../utils/utils';
import { containsObjectByKey } from '../../utils';
import { ManageMembers } from '../ManageMembers/ManageMembers';
import { ManageTags } from '../ManageTags';
import type { AddEditWorkspacePanelProps } from './types';

const stackItemStyles: IStackItemStyles = {
  root: {
    padding: '8px 0',
    flexGrow: 1,
  },
};

export const AddEditWorkspacePanel = (props: AddEditWorkspacePanelProps) => {
  const { onDismiss, isOpen, workspaceForEdit, onWorkspaceSave, availableTags, readOnly = false } = props;

  const {
    roleBindings = [],
    fetchRoleBindings,
    createRoleBinding,
    deleteRoleBinding,
    allRoles,
    loadingRoleBindings,
  } = useRoles();
  const user = useUser();
  const { addToast } = useToast();

  const defaultRole: RoleBinding | undefined = useMemo(() => {
    const workspaceOwnerRole = allRoles?.find((role) => role.name === 'roles/workspace-owner');
    if (!workspaceOwnerRole) return undefined;

    return {
      role: workspaceOwnerRole.name,
      subject: `users/${user.id}`,
    };
  }, [allRoles, user.id]);

  const [displayName, setDisplayName] = useState<string | undefined>('');
  const [description, setDescription] = useState<string | undefined>('');
  const [listedRoles, setListedRoles] = useState<RoleBinding[]>([]);
  const [createRolesBuffer, setCreateRolesBuffer] = useState<RoleBinding[]>([]);
  const [deleteRolesBuffer, setDeleteRolesBuffer] = useState<RoleBinding[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>(workspaceForEdit?.tags || []);
  const [addTagsBuffer, setAddTagsBuffer] = useState<string[]>([]);
  const [removeTagsBuffer, setRemoveTagsBuffer] = useState<string[]>([]);
  const [annotations, setAnnotations] = useState<Record<string, string>>({});
  const [showValidation, setShowValidation] = useState(false);

  const createWorkspaceMutation = useMutationCreateWorkspace();
  const updateWorkspaceMutation = useMutationUpdateWorkspace();
  const addTagsMutation = useMutationAddTagsToWorkspace();
  const removeTagsMutation = useMutationRemoveTagsFromWorkspace();

  const canViewAnnotateQuery = useQueryPermissionCurrentUser({
    action: WorkspacesUserActionType.WORKSPACE_GET,
    resource: workspaceForEdit?.name ? roleBindingResourceCompile(workspaceForEdit.name) : '//workspaceserver',
  });
  const canEditAnnotateQuery = useQueryPermissionCurrentUser({
    action: WorkspacesUserActionType.WORKSPACE_EDIT,
    resource: workspaceForEdit?.name ? roleBindingResourceCompile(workspaceForEdit.name) : '//workspaceserver',
  });
  const canViewAnnotate = Boolean(workspaceForEdit?.name && canViewAnnotateQuery.data?.allowed);
  const canEditAnnotate = Boolean(workspaceForEdit?.name && canEditAnnotateQuery.data?.allowed);

  const handleWorkspaceSave = useCallback(async () => {
    if (!displayName) {
      setShowValidation(true);
      return;
    }

    const workspacePayload = { workspace: { displayName, description } };
    let workspaceName = workspaceForEdit?.name;

    try {
      if (!workspaceName) {
        const response = await createWorkspaceMutation.mutateAsync(workspacePayload);
        workspaceName = response.workspace?.name;
      } else {
        const isWorkspaceUpdated =
          workspaceForEdit?.displayName !== displayName || workspaceForEdit?.description !== description;
        const annotationsChanged = JSON.stringify(workspaceForEdit?.annotations || {}) !== JSON.stringify(annotations);

        const updateMaskParts: string[] = [];
        if (isWorkspaceUpdated) {
          updateMaskParts.push('display_name', 'description');
        }
        if (annotationsChanged) {
          updateMaskParts.push('annotations');
        }

        if (updateMaskParts.length > 0) {
          await updateWorkspaceMutation.mutateAsync({
            workspace: {
              ...workspacePayload.workspace,
              name: workspaceName,
              ...(annotationsChanged ? { annotations } : {}),
            },
            updateMask: updateMaskParts.join(','),
          });
        }
      }

      await Promise.all(deleteRolesBuffer.map((binding) => deleteRoleBinding(binding)));

      await Promise.all(
        createRolesBuffer.map((binding) => createRoleBinding(binding.role || '', binding.subject || '', workspaceName))
      );

      if (workspaceName && addTagsBuffer.length > 0) {
        await addTagsMutation.mutateAsync({
          workspace: workspaceName,
          tags: addTagsBuffer,
        });
      }

      if (workspaceName && removeTagsBuffer.length > 0) {
        await removeTagsMutation.mutateAsync({
          workspace: workspaceName,
          tags: removeTagsBuffer,
        });
      }

      onWorkspaceSave();
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Workspace saved successfully.',
      });
    } catch (err) {
      const message = `Failed to save workspace: ${formatError(err)}`;
      console.error(message);
      addToast({ messageBarType: MessageBarType.error, message });
    }
  }, [
    displayName,
    description,
    workspaceForEdit,
    annotations,
    deleteRolesBuffer,
    createRolesBuffer,
    addTagsBuffer,
    removeTagsBuffer,
    onWorkspaceSave,
    addToast,
    createWorkspaceMutation,
    updateWorkspaceMutation,
    addTagsMutation,
    removeTagsMutation,
    deleteRoleBinding,
    createRoleBinding,
  ]);
  const handleMembersChange = useCallback(
    (roleBindingOptions: RoleBinding[], isCreate?: boolean) => {
      const filterOutRoles = (role: RoleBinding) => {
        return !roleBindingOptions.some((item) => item.role === role.role && item.subject === role.subject);
      };

      if (isCreate) {
        const isInDeleteList = containsObjectByKey(deleteRolesBuffer, roleBindingOptions, 'role', 'subject');

        if (!isInDeleteList) {
          // New role binding
          setCreateRolesBuffer((prev) => [...prev, ...roleBindingOptions]);
        } else {
          // Undo remove role binding
          // Use case:
          // - Deleted a role, but didn't save the change
          // - Added the role back
          setDeleteRolesBuffer((prev) => [...prev.filter(filterOutRoles)]);
        }
      } else {
        const isInCreateList = containsObjectByKey(createRolesBuffer, roleBindingOptions, 'role', 'subject');

        if (!isInCreateList) {
          // Delete an existing role binding
          setDeleteRolesBuffer((prev) => [...prev, ...roleBindingOptions]);
          setListedRoles((prev) => [...prev.filter(filterOutRoles)]);
        } else {
          // Remove an added but unsaved role binding
          setCreateRolesBuffer((prev) => [...prev.filter(filterOutRoles)]);
        }
      }
    },
    [createRolesBuffer, deleteRolesBuffer]
  );

  const handleTagChange = useCallback(
    (_e: unknown, option?: IDropdownOption) => {
      if (!option?.key) return;

      const tagKey = option.key as string;
      const isCurrentlySelected = selectedTags.includes(tagKey);
      const isOriginalTag = workspaceForEdit?.tags?.includes(tagKey);

      if (isCurrentlySelected) {
        setSelectedTags(selectedTags.filter((t) => t !== tagKey));

        if (isOriginalTag) {
          setRemoveTagsBuffer([...removeTagsBuffer, tagKey]);
        } else {
          setAddTagsBuffer(addTagsBuffer.filter((t) => t !== tagKey));
        }
      } else {
        setSelectedTags([...selectedTags, tagKey]);

        if (isOriginalTag) {
          setRemoveTagsBuffer(removeTagsBuffer.filter((t) => t !== tagKey));
        } else {
          setAddTagsBuffer([...addTagsBuffer, tagKey]);
        }
      }
    },
    [selectedTags, workspaceForEdit?.tags, addTagsBuffer, removeTagsBuffer]
  );

  // Track the workspace we're currently fetching role bindings for
  const currentWorkspaceNameRef = useRef<string | undefined>();

  // Fetch role bindings when panel opens with a workspace to edit
  useEffect(() => {
    if (isOpen && workspaceForEdit) {
      const workspaceName = workspaceForEdit.name || '';
      // Clear listed roles immediately when workspace changes to prevent showing stale data
      setListedRoles([]);
      currentWorkspaceNameRef.current = workspaceName;
      fetchRoleBindings(workspaceName);
    } else {
      currentWorkspaceNameRef.current = undefined;
    }
  }, [isOpen, workspaceForEdit, fetchRoleBindings]);

  // Update listed roles only when editing a workspace and roles are fetched
  useEffect(() => {
    const currentWorkspaceName = workspaceForEdit?.name;
    // Only update if we're still viewing the same workspace and loading is complete
    if (currentWorkspaceName && currentWorkspaceName === currentWorkspaceNameRef.current && !loadingRoleBindings) {
      if (roleBindings.length > 0) {
        setListedRoles(roleBindings);
      } else {
        // Clear listed roles if no role bindings found after loading completes
        setListedRoles([]);
      }
    }
  }, [workspaceForEdit, roleBindings, loadingRoleBindings]);

  // Reset and populate fields when panel opens
  useEffect(() => {
    if (isOpen) {
      if (workspaceForEdit) {
        setDisplayName(workspaceForEdit.displayName);
        setDescription(workspaceForEdit.description);
        setSelectedTags(workspaceForEdit.tags || []);
        setAnnotations(workspaceForEdit.annotations || {});
      } else {
        setDisplayName('');
        setDescription('');
        setSelectedTags([]);
        setAnnotations({});
      }
      setAddTagsBuffer([]);
      setRemoveTagsBuffer([]);
      setShowValidation(false);
    } else {
      // Clear buffers when panel closes
      setListedRoles([]);
      setCreateRolesBuffer([]);
      setDeleteRolesBuffer([]);
      setSelectedTags([]);
      setAddTagsBuffer([]);
      setRemoveTagsBuffer([]);
      setAnnotations({});
    }
  }, [isOpen, workspaceForEdit]);

  const headerText = readOnly ? 'View Workspace' : workspaceForEdit ? 'Edit Workspace' : 'Add New Workspace';

  return (
    <Panel
      isLightDismiss
      customWidth="700px"
      headerText={headerText}
      isFooterAtBottom
      isOpen={isOpen}
      onDismiss={onDismiss}
      type={PanelType.custom}
      onRenderFooterContent={() => (
        <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign="space-between">
          {!readOnly && <Button text="Save" styles={buttonStylesPrimary} onClick={handleWorkspaceSave} role="submit" />}
          <Button text="Close" onClick={onDismiss} />
        </Stack>
      )}
    >
      <Stack>
        <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign="stretch">
          <Stack styles={stackItemStyles}>
            <TextField
              label="Display Name"
              value={displayName}
              onChange={(_e, value) => setDisplayName(value || '')}
              required
              readOnly={readOnly}
              errorMessage={showValidation && !displayName ? 'Please enter a workspace name' : undefined}
            />
          </Stack>
          <Stack styles={stackItemStyles}>
            <TextField
              label="Description"
              value={description}
              onChange={(_e, value) => setDescription(value || '')}
              readOnly={readOnly}
            />
          </Stack>
        </Stack>

        {availableTags && (
          <>
            <Separator styles={{ root: { margin: '16px 0' } }}>Tags</Separator>

            <ManageTags
              selectedTags={selectedTags}
              availableTags={availableTags}
              onChange={handleTagChange}
              disabled={readOnly}
            />
          </>
        )}

        <Separator styles={{ root: { margin: '16px 0' } }}>Members and permissions</Separator>

        <Stack>
          <ManageMembers
            prepopulatedRoleBindings={[
              ...listedRoles,
              ...createRolesBuffer,
              // Show current user's workspace-owner role when creating new workspace.
              // This is for display only - the API automatically creates this role, so we don't add it to createRolesBuffer.
              ...(!workspaceForEdit && defaultRole ? [defaultRole] : []),
            ]}
            onMembersChange={handleMembersChange}
            readOnly={readOnly}
            loading={loadingRoleBindings && Boolean(workspaceForEdit)}
          />
        </Stack>

        {canViewAnnotate && (
          <Accordion
            title="Annotations"
            isClose
            styles={{
              title: { fontWeight: 400 },
              root: { marginTop: 24, height: 'unset !important' },
            }}
          >
            <KeyValuePairEditor
              onUpdateConfig={readOnly || !canEditAnnotate ? () => {} : (config) => setAnnotations(config || {})}
              config={annotations}
              readOnly={readOnly || !canEditAnnotate}
            />
          </Accordion>
        )}
      </Stack>
    </Panel>
  );
};
